"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { flushSync } from "react-dom";
import type { SbBlokData } from "@storyblok/react/rsc";
import { storyblokEditable } from "@/lib/storyblok-editable";

import { ANALYTICS_EVENTS, track } from "@/lib/analytics";
import { resolveBg } from "@/lib/bg-color";
import { posterFallback, youtubeThumbnail } from "@/lib/youtube";
import YouTubePlayer from "@/components/video/youtube-player";

type VideoItem = SbBlokData & {
  youtube_url?: string;
  quote?: string;
  name?: string;
};

type VideoTestimonialsBlok = SbBlokData & {
  title?: string;
  description?: string;
  videos?: VideoItem[];
  view_more_label?: string;
  bg_color?: unknown;
};

const COLLAPSED_MAX_H_MOBILE = 1000;
const COLLAPSED_MAX_H_DESKTOP = 1700;

function extractYouTubeId(url: string | undefined): string | null {
  if (!url) return null;
  const patterns = [
    /youtube\.com\/shorts\/([a-zA-Z0-9_-]{11})/,
    /youtube\.com\/watch\?v=([a-zA-Z0-9_-]{11})/,
    /youtu\.be\/([a-zA-Z0-9_-]{11})/,
    /youtube\.com\/embed\/([a-zA-Z0-9_-]{11})/,
  ];
  for (const p of patterns) {
    const m = url.match(p);
    if (m) return m[1];
  }
  return null;
}

function asQuote(text: string): string {
  const inner = text
    .trim()
    .replace(/^["'‘’“”]+|["'‘’“”]+$/g, "")
    .trim();
  return `‘${inner}’`;
}

function PlayButton({ className }: { className?: string }) {
  return (
    <span
      className={`bg-teal-07 flex size-[60px] items-center justify-center rounded-full text-white ${className ?? ""}`}
    >
      <svg viewBox="0 0 24 24" fill="currentColor" className="size-6 translate-x-px" aria-hidden>
        <path d="M8 5v14l11-7z" />
      </svg>
    </span>
  );
}

function VideoCard({
  video,
  onDark,
  isPlaying,
  dimmed,
  inline,
  onPlay,
  onState,
  posterRef,
  posterTabIndex,
  onPosterKeyDown,
  onPosterFocus,
  cardInert,
}: {
  video: VideoItem;
  onDark: boolean;
  isPlaying: boolean;
  dimmed: boolean;
  inline: boolean;
  onPlay: (uid: string) => void;
  onState: (uid: string, state: number) => void;
  posterRef?: (el: HTMLButtonElement | null) => void;
  posterTabIndex?: number;
  onPosterKeyDown?: React.KeyboardEventHandler<HTMLButtonElement>;
  onPosterFocus?: () => void;
  cardInert?: boolean;
}) {
  const id = extractYouTubeId(video.youtube_url);
  const uid = video._uid as string;

  const handleStateChange = useCallback(
    (state: "playing" | "paused" | "ended") => {
      const code = state === "playing" ? 1 : state === "paused" ? 2 : 0;
      onState(uid, code);
    },
    [onState, uid],
  );

  return (
    <div
      {...storyblokEditable(video)}
      data-video-card
      inert={cardInert}
      className={`flex flex-col gap-5 transition-opacity duration-300 ${
        dimmed ? "opacity-40 hover:opacity-100" : "opacity-100"
      }`}
    >
      <div className="relative aspect-video w-full overflow-hidden rounded-2xl bg-black">
        {isPlaying && id ? (
          <div className="absolute inset-0 size-full">
            <YouTubePlayer
              videoId={id}
              title={video.name}
              inline={inline}
              autoStart
              onStateChange={handleStateChange}
            />
          </div>
        ) : id ? (
          <button
            ref={posterRef}
            type="button"
            tabIndex={posterTabIndex}
            onKeyDown={onPosterKeyDown}
            onFocus={onPosterFocus}
            onClick={() => {
              track(ANALYTICS_EVENTS.VIDEO_PLAYED, {
                module: "Video Testimonials",
                video: video.name,
              });
              onPlay(uid);
            }}
            aria-label={`Play video${video.name ? ` — ${video.name}` : ""}`}
            className="video-poster-btn group absolute inset-0 size-full cursor-pointer"
          >
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src={youtubeThumbnail(id)}
              width={1280}
              height={720}
              onError={posterFallback}
              alt={video.name || "Patient video"}
              aria-hidden
              loading="lazy"
              className="absolute inset-0 size-full object-cover"
            />
            <span className="absolute inset-0 flex items-center justify-center">
              <PlayButton className="shadow-[0_8px_24px_-8px_rgba(0,0,0,0.45)] transition-transform group-hover:scale-105" />
            </span>
          </button>
        ) : null}
      </div>

      <div className="flex flex-1 flex-col gap-4">
        {video.quote && (
          <blockquote
            className={`text-lg leading-[27px] ${onDark ? "text-white/85" : "text-grey-08"}`}
          >
            {asQuote(video.quote)}
          </blockquote>
        )}
        {video.name && (
          <p className={`mt-auto font-bold ${onDark ? "text-white" : "text-grey-08"}`}>
            {video.name}
          </p>
        )}
      </div>
    </div>
  );
}

export default function VideoTestimonials({ blok }: { blok: VideoTestimonialsBlok }) {
  const { bg, onDark, hex } = resolveBg(blok.bg_color, "white");
  const videos = useMemo(() => blok.videos ?? [], [blok.videos]);

  const [playingUid, setPlayingUid] = useState<string | null>(null);
  const [activeUid, setActiveUid] = useState<string | null>(null);
  const [inline, setInline] = useState(true);
  const [expanded, setExpanded] = useState(false);

  const [overflows, setOverflows] = useState(true);
  const [aboveFoldCount, setAboveFoldCount] = useState(videos.length);
  const [activeIndex, setActiveIndex] = useState(0);
  const gridRef = useRef<HTMLDivElement>(null);
  const posterRefs = useRef<Map<string, HTMLButtonElement | null>>(new Map());

  useEffect(() => {
    if (!playingUid) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape" || document.fullscreenElement) return;
      const uid = playingUid;
      setPlayingUid(null);
      setActiveUid(null);
      requestAnimationFrame(() => posterRefs.current.get(uid)?.focus());
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [playingUid]);

  useEffect(() => {
    const el = gridRef.current;
    if (!el) return;
    const check = () => {
      const limit = window.innerWidth >= 1115 ? COLLAPSED_MAX_H_DESKTOP : COLLAPSED_MAX_H_MOBILE;
      setOverflows(el.offsetHeight > limit + 8);
      const gridTop = el.getBoundingClientRect().top;
      const cards = Array.from(el.querySelectorAll<HTMLElement>("[data-video-card]"));
      const above = cards.filter((c) => c.getBoundingClientRect().top - gridTop < limit - 8).length;
      setAboveFoldCount(above || cards.length);
    };
    check();
    const observer = new ResizeObserver(check);
    observer.observe(el);
    window.addEventListener("resize", check);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", check);
    };
  }, [videos.length]);

  const collapsed = !expanded && overflows;
  const visibleCount = collapsed ? aboveFoldCount : videos.length;
  const roveIndex = Math.min(activeIndex, Math.max(0, visibleCount - 1));

  const handlePosterKey = useCallback(
    (e: React.KeyboardEvent<HTMLButtonElement>, index: number) => {
      const total = visibleCount;
      if (total === 0) return;
      let next = index;
      if (e.key === "ArrowRight" || e.key === "ArrowDown") next = Math.min(index + 1, total - 1);
      else if (e.key === "ArrowLeft" || e.key === "ArrowUp") next = Math.max(index - 1, 0);
      else if (e.key === "Home") next = 0;
      else if (e.key === "End") next = total - 1;
      else return;
      e.preventDefault();
      setActiveIndex(next);
      const uid = videos[next]?._uid as string | undefined;
      if (uid) posterRefs.current.get(uid)?.focus();
    },
    [visibleCount, videos],
  );

  const handleViewMore = useCallback(() => {
    const grid = gridRef.current;
    const limit = window.innerWidth >= 1115 ? COLLAPSED_MAX_H_DESKTOP : COLLAPSED_MAX_H_MOBILE;

    flushSync(() => setExpanded(true));
    if (!grid) return;
    const gridTop = grid.getBoundingClientRect().top;
    const buttons = Array.from(grid.querySelectorAll<HTMLElement>(".video-poster-btn"));
    const firstBelow = buttons.find(
      (btn) => btn.getBoundingClientRect().top - gridTop >= limit - 8,
    );
    (firstBelow ?? buttons[0])?.focus();
  }, []);

  const handlePlay = useCallback((uid: string) => {
    setInline(window.matchMedia("(min-width: 768px)").matches);
    setPlayingUid(uid);
    setActiveUid(uid);
  }, []);

  const handleState = useCallback((uid: string, state: number) => {
    if (state === 1) {
      setActiveUid(uid);
    } else if (state === 0 || state === 2) {
      setActiveUid((prev) => (prev === uid ? null : prev));
    }
  }, []);

  return (
    <section {...storyblokEditable(blok)} className={`${bg} px-5 py-10 md:px-10 lg:px-20 lg:py-20`}>
      <div className="mx-auto w-full max-w-7xl">
        <div className="flex flex-col gap-4">
          {blok.title && (
            <h2 className={`text-section ${onDark ? "text-white" : "text-teal-07"}`}>
              {blok.title}
            </h2>
          )}
          {blok.description && (
            <p
              className={`max-w-[687px] text-base leading-normal lg:text-xl ${
                onDark ? "text-white/85" : "text-grey-06"
              }`}
            >
              {blok.description}
            </p>
          )}
        </div>

        {videos.length > 0 && (
          <div className="relative mt-[clamp(2rem,4vw,3.5rem)]">
            <div className={collapsed ? "max-h-250 overflow-hidden lg:max-h-425" : ""}>
              <div ref={gridRef} className="grid grid-cols-1 gap-x-6 gap-y-10 md:grid-cols-2">
                {videos.map((video, index) => {
                  const uid = video._uid as string;
                  return (
                    <VideoCard
                      key={uid}
                      video={video}
                      onDark={onDark}
                      isPlaying={playingUid === uid}
                      dimmed={activeUid !== null && activeUid !== uid}
                      inline={inline}
                      onPlay={handlePlay}
                      onState={handleState}
                      posterRef={(el) => posterRefs.current.set(uid, el)}
                      posterTabIndex={index === roveIndex ? 0 : -1}
                      onPosterKeyDown={(e) => handlePosterKey(e, index)}
                      onPosterFocus={() => setActiveIndex(index)}
                      cardInert={collapsed && index >= aboveFoldCount}
                    />
                  );
                })}
              </div>
            </div>
            {collapsed && (
              <div
                aria-hidden
                className="pointer-events-none absolute inset-x-0 bottom-0 h-48"
                style={{ background: `linear-gradient(to top, ${hex}, transparent)` }}
              />
            )}
          </div>
        )}

        {collapsed && videos.length > 0 && (
          <div className="mt-8 flex justify-center">
            <button
              type="button"
              onClick={handleViewMore}
              className={`inline-flex cursor-pointer items-center gap-1.5 font-bold hover:underline ${
                onDark ? "text-white" : "text-teal-07"
              }`}
            >
              {blok.view_more_label || "View more"}
              <span aria-hidden className="leading-none">
                +
              </span>
            </button>
          </div>
        )}
      </div>
    </section>
  );
}
